import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { colors } from '../../styles/globalStyles';

const MESES = ['Janeiro','Fevereiro','Março','Abril','Maio','Junho','Julho','Agosto','Setembro','Outubro','Novembro','Dezembro'];
const STATUS_CFG = {
  realizada:   { label:'Realizada',   icon:'✅', color:'#0b8043', bg:'#e6f4ea', border:'#34a853' },
  falta:       { label:'Falta',       icon:'❌', color:'#c5221f', bg:'#fce8e6', border:'#ea4335' },
  cancelou:    { label:'Cancelou',    icon:'📅', color:'#e37400', bg:'#fef7e0', border:'#fbbc04' },
  recuperacao: { label:'Recuperação', icon:'🔄', color:'#d56e0c', bg:'#fdebd0', border:'#f09300' },
};

const PRIMARY = colors.primary || '#0f5a30';

// ── helpers ───────────────────────────────────────────────────────────────────
const mesAtual = () => new Date().toISOString().slice(0, 7);

const fmtMes = (yyyy_mm) => {
  if (!yyyy_mm) return '';
  const [y, m] = yyyy_mm.split('-');
  return `${MESES[parseInt(m) - 1]} ${y}`;
};

const fmtData = (iso) => {
  const d = new Date(iso);
  return `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}`;
};

const fmtDataLonga = (iso) => {
  const d = new Date(iso);
  const dias = ['Dom','Seg','Ter','Qua','Qui','Sex','Sáb'];
  return `${dias[d.getDay()]} ${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}`;
};

const mesPrevMeses = (qtd) => {
  const opts = [];
  for (let i = 0; i < qtd; i++) {
    const d = new Date();
    d.setMonth(d.getMonth() - i);
    opts.push(d.toISOString().slice(0, 7));
  }
  return opts;
};

// ── sub-componentes ───────────────────────────────────────────────────────────

const StatCard = ({ label, value, sub, color, bg }) => (
  <div style={{
    background: bg || '#f8fafc', borderRadius: '12px', padding: '14px 16px',
    border: `1px solid ${color ? color + '33' : '#e2e8f0'}`, textAlign: 'center',
    flex: '1 1 100px',
  }}>
    <div style={{ fontSize: '22px', fontWeight: 800, color: color || '#1e293b', lineHeight: 1.1 }}>
      {value ?? '—'}
    </div>
    <div style={{ fontSize: '11px', fontWeight: 600, color: '#64748b', marginTop: '3px', lineHeight: 1.3 }}>{label}</div>
    {sub !== undefined && (
      <div style={{ fontSize: '10px', color: '#94a3b8', marginTop: '2px' }}>{sub}</div>
    )}
  </div>
);

const SeletorMes = ({ value, onChange, opcoes }) => (
  <select
    value={value}
    onChange={e => onChange(e.target.value)}
    style={{
      padding: '7px 12px', borderRadius: '8px', border: '1px solid #d1d5db',
      fontSize: '14px', background: 'white', cursor: 'pointer', color: '#374151',
    }}
  >
    {opcoes.map(m => (
      <option key={m} value={m}>{fmtMes(m)}</option>
    ))}
  </select>
);

// ── Modal Editar Plano ────────────────────────────────────────────────────────
const ModalEditarPlano = ({ aluno, mes, planoAtual, onSalvar, onClose }) => {
  const [previstas, setPrevistas] = useState(planoAtual?.aulas_previstas ?? '');
  const [descricao, setDescricao] = useState(planoAtual?.plano_descricao ?? '');
  const [freqSem, setFreqSem]     = useState(planoAtual?.frequencia_semanal ?? '');
  const [obs, setObs]             = useState(planoAtual?.observacao ?? '');
  const [salvando, setSalvando]   = useState(false);

  const handleSalvar = async () => {
    if (!previstas || parseInt(previstas) < 0) { alert('Informe a quantidade de aulas previstas'); return; }
    setSalvando(true);
    await onSalvar({ aulas_previstas: parseInt(previstas), plano_descricao: descricao, frequencia_semanal: freqSem ? parseInt(freqSem) : null, observacao: obs });
    setSalvando(false);
  };

  return (
    <>
      <div onClick={onClose} style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', zIndex:5000 }} />
      <div style={{
        position:'fixed', top:'50%', left:'50%', transform:'translate(-50%,-50%)',
        background:'white', borderRadius:'16px', padding:'28px', width:'440px',
        maxWidth:'94vw', zIndex:5001, boxShadow:'0 20px 60px rgba(0,0,0,0.25)',
      }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'20px' }}>
          <div>
            <div style={{ fontWeight:700, fontSize:'16px', color:'#1e293b' }}>Plano Mensal</div>
            <div style={{ fontSize:'13px', color:'#64748b' }}>{aluno.aluno_nome} · {fmtMes(mes)}</div>
          </div>
          <button onClick={onClose} style={{ background:'none', border:'none', fontSize:'20px', cursor:'pointer', color:'#94a3b8' }}>✕</button>
        </div>

        {[
          { label:'Aulas previstas no mês *', type:'number', value:previstas, set:setPrevistas, min:0, placeholder:'Ex: 8' },
          { label:'Descrição do plano', type:'text', value:descricao, set:setDescricao, placeholder:'Ex: 2x por semana · 8 aulas' },
        ].map(f => (
          <div key={f.label} style={{ marginBottom:'14px' }}>
            <label style={{ display:'block', fontSize:'12px', fontWeight:600, color:'#475569', marginBottom:'5px' }}>{f.label}</label>
            <input
              type={f.type} value={f.value} min={f.min}
              onChange={e => f.set(e.target.value)} placeholder={f.placeholder}
              style={{ width:'100%', padding:'9px 12px', border:'1px solid #d1d5db', borderRadius:'8px', fontSize:'14px', boxSizing:'border-box' }}
            />
          </div>
        ))}

        <div style={{ marginBottom:'14px' }}>
          <label style={{ display:'block', fontSize:'12px', fontWeight:600, color:'#475569', marginBottom:'5px' }}>Frequência semanal</label>
          <select
            value={freqSem} onChange={e => setFreqSem(e.target.value)}
            style={{ width:'100%', padding:'9px 12px', border:'1px solid #d1d5db', borderRadius:'8px', fontSize:'14px' }}
          >
            <option value="">Não definida</option>
            {[1,2,3,4,5].map(n => <option key={n} value={n}>{n}x por semana</option>)}
          </select>
        </div>

        <div style={{ marginBottom:'20px' }}>
          <label style={{ display:'block', fontSize:'12px', fontWeight:600, color:'#475569', marginBottom:'5px' }}>Observação</label>
          <textarea
            value={obs} onChange={e => setObs(e.target.value)} rows={2}
            placeholder="Observações sobre este mês..."
            style={{ width:'100%', padding:'9px 12px', border:'1px solid #d1d5db', borderRadius:'8px', fontSize:'14px', resize:'vertical', boxSizing:'border-box' }}
          />
        </div>

        <div style={{ display:'flex', gap:'10px' }}>
          <button onClick={onClose} style={{ flex:1, padding:'11px', border:'1px solid #d1d5db', borderRadius:'8px', background:'white', cursor:'pointer', fontSize:'14px', fontWeight:600 }}>
            Cancelar
          </button>
          <button onClick={handleSalvar} disabled={salvando} style={{
            flex:2, padding:'11px', border:'none', borderRadius:'8px',
            background: PRIMARY, color:'white', fontWeight:700, fontSize:'14px', cursor:'pointer',
            opacity: salvando ? 0.7 : 1,
          }}>
            {salvando ? 'Salvando...' : '💾 Salvar Plano'}
          </button>
        </div>
      </div>
    </>
  );
};

// ── Painel individual do aluno ────────────────────────────────────────────────
const PainelAluno = ({ aluno, createApi, isMobile, onBack }) => {
  const [mes, setMes]             = useState(mesAtual());
  const [dados, setDados]         = useState(null);
  const [historico, setHistorico] = useState(null);
  const [loading, setLoading]     = useState(false);
  const [loadingHist, setLoadingHist] = useState(false);
  const [subView, setSubView]     = useState('painel'); // 'painel' | 'historico'
  const [modalPlano, setModalPlano] = useState(false);
  const [erro, setErro]           = useState('');

  const opcoesMes = useMemo(() => mesPrevMeses(18), []);

  const carregarPainel = useCallback(async () => {
    setLoading(true); setErro('');
    try {
      const api = createApi();
      const { data } = await api.get(`/admin/alunos-fixos/painel?aluno_id=${aluno.aluno_id}&mes=${mes}`);
      setDados(data);
    } catch { setErro('Erro ao carregar painel'); }
    finally { setLoading(false); }
  }, [createApi, aluno.aluno_id, mes]);

  const carregarHistorico = useCallback(async () => {
    if (historico) return;
    setLoadingHist(true);
    try {
      const api = createApi();
      const { data } = await api.get(`/admin/alunos-fixos/historico/${aluno.aluno_id}`);
      setHistorico(data);
    } catch { setErro('Erro ao carregar histórico'); }
    finally { setLoadingHist(false); }
  }, [createApi, aluno.aluno_id, historico]);

  useEffect(() => { carregarPainel(); }, [carregarPainel]);

  useEffect(() => {
    if (subView === 'historico') carregarHistorico();
  }, [subView, carregarHistorico]);

  const salvarPlano = async (planoData) => {
    try {
      const api = createApi();
      await api.put('/admin/alunos-fixos/plano-mensal', {
        aluno_id: aluno.aluno_id,
        mes_competencia: mes + '-01',
        ...planoData,
      });
      setModalPlano(false);
      setDados(null);
      carregarPainel();
    } catch { setErro('Erro ao salvar plano'); }
  };

  const stats = dados?.stats;
  const plano = dados?.plano;
  const registros = dados?.registros || [];
  const registrosPorStatus = useMemo(() => {
    const m = {};
    registros.forEach(r => {
      if (!m[r.status]) m[r.status] = [];
      m[r.status].push(r);
    });
    return m;
  }, [registros]);

  return (
    <div style={{ padding: isMobile ? '12px' : '20px', maxWidth: '800px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display:'flex', alignItems:'center', gap:'12px', marginBottom:'20px', flexWrap:'wrap' }}>
        <button onClick={onBack} style={{
          background:'none', border:'1px solid #d1d5db', borderRadius:'8px',
          padding:'6px 12px', cursor:'pointer', fontSize:'13px', color:'#374151', fontWeight:600,
        }}>← Voltar</button>
        <div style={{ flex:1 }}>
          <div style={{ fontWeight:700, fontSize:'18px', color:'#1e293b' }}>{aluno.aluno_nome}</div>
          <div style={{ fontSize:'13px', color:'#64748b' }}>{aluno.aluno_email}</div>
        </div>
      </div>

      {/* Sub-tabs */}
      <div style={{ display:'flex', gap:'4px', borderBottom:'1px solid #e2e8f0', marginBottom:'20px' }}>
        {[{ id:'painel', label:'📊 Painel Mensal' }, { id:'historico', label:'📅 Histórico' }].map(t => (
          <button key={t.id} onClick={() => setSubView(t.id)} style={{
            padding:'8px 16px', border:'none', cursor:'pointer', fontSize:'14px',
            fontWeight: subView === t.id ? 700 : 500,
            background: subView === t.id ? 'white' : 'transparent',
            color: subView === t.id ? PRIMARY : '#64748b',
            borderBottom: subView === t.id ? `2px solid ${PRIMARY}` : '2px solid transparent',
            borderRadius:'6px 6px 0 0',
          }}>{t.label}</button>
        ))}
      </div>

      {erro && <div style={{ padding:'10px 14px', background:'#fef2f2', color:'#dc2626', borderRadius:'8px', marginBottom:'16px', fontSize:'13px' }}>{erro}</div>}

      {/* ── PAINEL MENSAL ── */}
      {subView === 'painel' && (
        <>
          {/* Seletor mês + editar plano */}
          <div style={{ display:'flex', alignItems:'center', gap:'10px', marginBottom:'16px', flexWrap:'wrap' }}>
            <SeletorMes value={mes} onChange={m => { setMes(m); setDados(null); }} opcoes={opcoesMes} />
            <button onClick={() => setModalPlano(true)} style={{
              padding:'7px 14px', border:`1px solid ${PRIMARY}`, borderRadius:'8px',
              background:'white', color: PRIMARY, fontWeight:600, cursor:'pointer', fontSize:'13px',
            }}>✏️ Definir Plano</button>
          </div>

          {/* Plano ativo */}
          {plano && (
            <div style={{ padding:'12px 16px', background:'#f0fdf4', border:'1px solid #86efac', borderRadius:'10px', marginBottom:'16px', fontSize:'13px' }}>
              <span style={{ fontWeight:700, color:'#15803d' }}>Plano: </span>
              <span style={{ color:'#166534' }}>
                {plano.plano_descricao || `${plano.frequencia_semanal ? plano.frequencia_semanal + 'x/sem · ' : ''}${plano.aulas_previstas} aulas`}
              </span>
              {plano.observacao && <span style={{ color:'#4ade80', marginLeft:'8px' }}>· {plano.observacao}</span>}
            </div>
          )}
          {!plano && !loading && (
            <div style={{ padding:'10px 14px', background:'#fefce8', border:'1px solid #fde047', borderRadius:'10px', marginBottom:'16px', fontSize:'13px', color:'#854d0e' }}>
              Plano mensal não definido. Clique em <strong>Definir Plano</strong> para configurar as aulas previstas.
            </div>
          )}

          {loading ? (
            <div style={{ textAlign:'center', padding:'32px', color:'#64748b' }}>Carregando...</div>
          ) : stats && (
            <>
              {/* Cards de métricas */}
              <div style={{ display:'flex', gap:'10px', flexWrap:'wrap', marginBottom:'20px' }}>
                <StatCard label="Previstas" value={plano?.aulas_previstas ?? '—'} color={PRIMARY} bg='#f0fdf4' />
                <StatCard label="Realizadas" value={stats.realizadas} color='#0b8043' bg='#e6f4ea' />
                <StatCard label="Cancelamentos" value={stats.cancelamentos} color='#e37400' bg='#fef7e0' />
                <StatCard label="Faltas" value={stats.faltas} color='#c5221f' bg='#fce8e6' />
                <StatCard label="Recuperações" value={stats.recuperacoes} color='#d56e0c' bg='#fdebd0' />
                <StatCard label="Créditos Disponíveis" value={stats.creditosDisponiveis} color='#1d4ed8' bg='#eff6ff'
                  sub="acumulado geral" />
                <StatCard label="Aulas Restantes" value={stats.aulasRestantes ?? '—'} color={PRIMARY} bg='#f0fdf4'
                  sub="previstas − realizadas" />
              </div>

              {/* Linha explicativa */}
              {plano && (
                <div style={{ padding:'10px 14px', background:'#f8fafc', borderRadius:'8px', marginBottom:'20px', fontSize:'12px', color:'#64748b', lineHeight:1.6 }}>
                  <strong>Como calcular:</strong> {plano.aulas_previstas} previstas − {stats.realizadas} realizadas = <strong>{stats.aulasRestantes} restantes</strong> · {stats.cancelamentos} cancelamento(s) geraram {stats.cancelamentos} crédito(s) · Créditos acumulados disponíveis: <strong>{stats.creditosDisponiveis}</strong>
                </div>
              )}

              {/* Timeline de aulas do mês */}
              {registros.length > 0 && (
                <div style={{ marginBottom:'20px' }}>
                  <div style={{ fontWeight:700, fontSize:'13px', color:'#475569', marginBottom:'10px', textTransform:'uppercase', letterSpacing:'0.05em' }}>
                    Aulas de {fmtMes(mes)}
                  </div>
                  <div style={{ display:'flex', flexWrap:'wrap', gap:'6px' }}>
                    {registros.map((r, i) => {
                      const cfg = STATUS_CFG[r.status];
                      return (
                        <div key={i} title={r.observacao || ''} style={{
                          padding:'5px 10px', borderRadius:'20px', fontSize:'12px', fontWeight:600,
                          background: cfg.bg, color: cfg.color, border:`1px solid ${cfg.border}`,
                          display:'flex', alignItems:'center', gap:'5px',
                        }}>
                          <span>{cfg.icon}</span>
                          <span>{fmtDataLonga(r.data_hora)}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Por status */}
              {Object.entries(STATUS_CFG).map(([key, cfg]) => {
                const lista = registrosPorStatus[key];
                if (!lista?.length) return null;
                return (
                  <div key={key} style={{ marginBottom:'14px' }}>
                    <div style={{ fontSize:'12px', fontWeight:700, color: cfg.color, marginBottom:'6px' }}>
                      {cfg.icon} {cfg.label} ({lista.length})
                    </div>
                    <div style={{ display:'flex', flexWrap:'wrap', gap:'4px' }}>
                      {lista.map((r, i) => (
                        <span key={i} style={{ fontSize:'12px', padding:'3px 8px', background: cfg.bg, color: cfg.color, borderRadius:'12px', border:`1px solid ${cfg.border}` }}>
                          {fmtData(r.data_hora)}
                          {r.falta_justificada !== null && r.falta_justificada !== undefined && (
                            <span style={{ marginLeft:'4px', opacity:0.7 }}>{r.falta_justificada ? '(J)' : '(NJ)'}</span>
                          )}
                        </span>
                      ))}
                    </div>
                  </div>
                );
              })}

              {registros.length === 0 && (
                <div style={{ textAlign:'center', padding:'24px', color:'#94a3b8', fontSize:'14px' }}>
                  Nenhuma aula registrada em {fmtMes(mes)}
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* ── HISTÓRICO ── */}
      {subView === 'historico' && (
        loadingHist ? (
          <div style={{ textAlign:'center', padding:'32px', color:'#64748b' }}>Carregando histórico...</div>
        ) : historico ? (
          <>
            <div style={{ display:'flex', gap:'12px', marginBottom:'20px', padding:'12px 16px', background:'#eff6ff', borderRadius:'10px', border:'1px solid #bfdbfe' }}>
              <div style={{ textAlign:'center' }}>
                <div style={{ fontWeight:800, fontSize:'20px', color:'#1d4ed8' }}>{historico.creditos_disponiveis}</div>
                <div style={{ fontSize:'11px', color:'#1e40af', fontWeight:600 }}>Créditos Disponíveis</div>
              </div>
              <div style={{ fontSize:'12px', color:'#1d4ed8', alignSelf:'center' }}>
                Total acumulado de créditos de recuperação disponíveis (cancelamentos minus recuperações já realizadas)
              </div>
            </div>

            {historico.historico.length === 0 && (
              <div style={{ textAlign:'center', padding:'32px', color:'#94a3b8' }}>Nenhum registro encontrado.</div>
            )}

            {historico.historico.map((h, i) => {
              const mesStr = h.mes_competencia.slice(0, 7);
              const aulas = (h.aulas || []);
              return (
                <div key={i} style={{
                  border:'1px solid #e2e8f0', borderRadius:'12px', marginBottom:'12px', overflow:'hidden',
                }}>
                  {/* Header do mês */}
                  <div style={{
                    padding:'10px 16px', background:'#f8fafc',
                    borderBottom:'1px solid #e2e8f0', display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:'8px',
                  }}>
                    <div style={{ fontWeight:700, color:'#1e293b', fontSize:'15px' }}>{fmtMes(mesStr)}</div>
                    {h.plano_descricao && (
                      <span style={{ fontSize:'12px', background:'#dcfce7', color:'#15803d', padding:'2px 8px', borderRadius:'10px', fontWeight:600 }}>
                        {h.plano_descricao}
                      </span>
                    )}
                  </div>

                  {/* Stats do mês */}
                  <div style={{ padding:'12px 16px' }}>
                    <div style={{ display:'flex', gap:'8px', flexWrap:'wrap', marginBottom: aulas.length ? '12px' : 0 }}>
                      {[
                        { label:'Previstas', value: h.aulas_previstas ?? '—', color:'#374151' },
                        { label:'Realizadas', value: parseInt(h.realizadas||0), color:'#0b8043' },
                        { label:'Cancelamentos', value: parseInt(h.cancelamentos||0), color:'#e37400' },
                        { label:'Faltas', value: parseInt(h.faltas||0), color:'#c5221f' },
                        { label:'Recuperações', value: parseInt(h.recuperacoes||0), color:'#d56e0c' },
                        { label:'Créditos gerados', value: parseInt(h.creditos_gerados||0), color:'#1d4ed8' },
                        h.aulas_previstas ? { label:'Restantes', value: Math.max(0, (h.aulas_previstas||0) - parseInt(h.realizadas||0)), color: PRIMARY } : null,
                      ].filter(Boolean).map((s, j) => (
                        <div key={j} style={{ textAlign:'center', minWidth:'60px' }}>
                          <div style={{ fontSize:'18px', fontWeight:800, color: s.color }}>{s.value}</div>
                          <div style={{ fontSize:'10px', color:'#94a3b8', fontWeight:600 }}>{s.label}</div>
                        </div>
                      ))}
                    </div>

                    {/* Datas */}
                    {aulas.length > 0 && (
                      <div style={{ display:'flex', flexWrap:'wrap', gap:'4px' }}>
                        {aulas.map((a, j) => {
                          const cfg = STATUS_CFG[a.status];
                          if (!cfg) return null;
                          return (
                            <span key={j} title={a.observacao || ''} style={{
                              fontSize:'11px', padding:'2px 7px', borderRadius:'10px',
                              background: cfg.bg, color: cfg.color, border:`1px solid ${cfg.border}`,
                            }}>
                              {cfg.icon} {fmtData(a.data)}
                            </span>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </>
        ) : null
      )}

      {/* Modal editar plano */}
      {modalPlano && (
        <ModalEditarPlano
          aluno={aluno} mes={mes} planoAtual={plano}
          onSalvar={salvarPlano} onClose={() => setModalPlano(false)}
        />
      )}
    </div>
  );
};

// ── Lista geral de alunos fixos ────────────────────────────────────────────────
const ListaAlunos = ({ alunos, resumo, isMobile, onSelect }) => {
  const [busca, setBusca] = useState('');

  const filtrados = useMemo(() => {
    const q = busca.toLowerCase();
    return alunos.filter(a => a.aluno_nome.toLowerCase().includes(q) || a.aluno_email?.toLowerCase().includes(q));
  }, [alunos, busca]);

  const resumoMap = useMemo(() => {
    const m = {};
    (resumo || []).forEach(r => { m[r.aluno_id] = r; });
    return m;
  }, [resumo]);

  if (!alunos.length) return (
    <div style={{ textAlign:'center', padding:'48px', color:'#94a3b8' }}>
      Nenhum aluno fixo cadastrado.
    </div>
  );

  return (
    <>
      <div style={{ padding: isMobile ? '12px' : '16px 20px', borderBottom:'1px solid #f1f5f9' }}>
        <input
          value={busca} onChange={e => setBusca(e.target.value)}
          placeholder="Buscar aluno..."
          style={{ width:'100%', padding:'9px 14px', border:'1px solid #d1d5db', borderRadius:'10px', fontSize:'14px', boxSizing:'border-box' }}
        />
      </div>
      <div style={{ padding: isMobile ? '12px' : '16px 20px' }}>
        {filtrados.map(aluno => {
          const r = resumoMap[aluno.aluno_id];
          const temPlano = r?.aulas_previstas != null;
          return (
            <div
              key={aluno.aluno_id}
              onClick={() => onSelect(aluno)}
              style={{
                display:'flex', alignItems:'center', gap:'14px', padding:'14px 16px',
                border:'1px solid #e2e8f0', borderRadius:'12px', marginBottom:'8px',
                cursor:'pointer', background:'white', transition:'all 0.12s',
              }}
              onMouseEnter={e => e.currentTarget.style.borderColor = PRIMARY}
              onMouseLeave={e => e.currentTarget.style.borderColor = '#e2e8f0'}
            >
              {/* Avatar */}
              <div style={{
                width:'40px', height:'40px', borderRadius:'50%',
                background: PRIMARY + '22', display:'flex', alignItems:'center', justifyContent:'center',
                flexShrink:0, fontWeight:700, fontSize:'16px', color: PRIMARY,
              }}>
                {aluno.aluno_nome.charAt(0).toUpperCase()}
              </div>

              {/* Info */}
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ fontWeight:700, color:'#1e293b', fontSize:'14px', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
                  {aluno.aluno_nome}
                </div>
                <div style={{ fontSize:'12px', color:'#64748b' }}>
                  {aluno.aluno_email}
                </div>
                {r?.plano_descricao && (
                  <div style={{ fontSize:'11px', color:'#15803d', fontWeight:600 }}>{r.plano_descricao}</div>
                )}
              </div>

              {/* Stats rápidos */}
              {r && (
                <div style={{ display:'flex', gap:'8px', flexShrink:0, flexWrap:'wrap', justifyContent:'flex-end' }}>
                  {[
                    { v: r.realizadas, c:'#0b8043', bg:'#e6f4ea', t:'Real.' },
                    { v: r.cancelamentos, c:'#e37400', bg:'#fef7e0', t:'Can.' },
                    { v: r.faltas, c:'#c5221f', bg:'#fce8e6', t:'Falt.' },
                    { v: r.creditos_disponiveis, c:'#1d4ed8', bg:'#eff6ff', t:'Cred.' },
                    temPlano ? { v: r.aulas_restantes ?? '?', c: PRIMARY, bg:'#f0fdf4', t:'Rest.' } : null,
                  ].filter(Boolean).map((s, i) => (
                    <div key={i} style={{ textAlign:'center' }}>
                      <div style={{
                        fontSize:'14px', fontWeight:800, color: s.c, padding:'2px 7px',
                        background: s.bg, borderRadius:'8px', minWidth:'28px',
                      }}>{s.v}</div>
                      <div style={{ fontSize:'9px', color:'#94a3b8', fontWeight:600 }}>{s.t}</div>
                    </div>
                  ))}
                </div>
              )}

              <span style={{ color:'#94a3b8', fontSize:'16px', flexShrink:0 }}>›</span>
            </div>
          );
        })}
      </div>
    </>
  );
};

// ── Relatório mensal ──────────────────────────────────────────────────────────
const Relatorio = ({ createApi, isMobile }) => {
  const [mes, setMes]         = useState(mesAtual());
  const [dados, setDados]     = useState(null);
  const [loading, setLoading] = useState(false);
  const [erro, setErro]       = useState('');
  const opcoesMes = useMemo(() => mesPrevMeses(18), []);

  const carregar = useCallback(async () => {
    setLoading(true); setErro('');
    try {
      const api = createApi();
      const { data } = await api.get(`/admin/relatorios/frequencia-mensal?mes=${mes}`);
      setDados(data.relatorio);
    } catch { setErro('Erro ao carregar relatório'); }
    finally { setLoading(false); }
  }, [createApi, mes]);

  useEffect(() => { carregar(); }, [carregar]);

  const COLUNAS = [
    { key:'nome', label:'Aluno' },
    { key:'plano_descricao', label:'Plano' },
    { key:'aulas_previstas', label:'Prev.' },
    { key:'realizadas', label:'Real.' },
    { key:'cancelamentos', label:'Can.' },
    { key:'faltas', label:'Falt.' },
    { key:'recuperacoes', label:'Recup.' },
    { key:'creditos_gerados_mes', label:'Cred.+' },
    { key:'creditos_disponiveis', label:'Cred.Disp.' },
    { key:'aulas_restantes', label:'Rest.' },
  ];

  const totais = useMemo(() => {
    if (!dados?.length) return null;
    return {
      aulas_previstas:      dados.reduce((s, r) => s + (parseInt(r.aulas_previstas)||0), 0),
      realizadas:           dados.reduce((s, r) => s + parseInt(r.realizadas||0), 0),
      cancelamentos:        dados.reduce((s, r) => s + parseInt(r.cancelamentos||0), 0),
      faltas:               dados.reduce((s, r) => s + parseInt(r.faltas||0), 0),
      recuperacoes:         dados.reduce((s, r) => s + parseInt(r.recuperacoes||0), 0),
      creditos_gerados_mes: dados.reduce((s, r) => s + parseInt(r.creditos_gerados_mes||0), 0),
      creditos_disponiveis: dados.reduce((s, r) => s + parseInt(r.creditos_disponiveis||0), 0),
      aulas_restantes:      dados.filter(r => r.aulas_restantes != null).reduce((s, r) => s + parseInt(r.aulas_restantes||0), 0),
    };
  }, [dados]);

  return (
    <div style={{ padding: isMobile ? '12px' : '20px' }}>
      <div style={{ display:'flex', gap:'10px', alignItems:'center', marginBottom:'20px', flexWrap:'wrap' }}>
        <SeletorMes value={mes} onChange={setMes} opcoes={opcoesMes} />
        <div style={{ fontSize:'14px', color:'#64748b' }}>Relatório de frequência dos alunos fixos</div>
      </div>

      {erro && <div style={{ padding:'10px', background:'#fef2f2', color:'#dc2626', borderRadius:'8px', marginBottom:'12px' }}>{erro}</div>}
      {loading && <div style={{ textAlign:'center', padding:'32px', color:'#64748b' }}>Carregando...</div>}

      {!loading && dados && (
        isMobile ? (
          // Mobile: cards
          <div>
            {dados.map((r, i) => (
              <div key={i} style={{ border:'1px solid #e2e8f0', borderRadius:'12px', padding:'14px', marginBottom:'10px' }}>
                <div style={{ fontWeight:700, color:'#1e293b', marginBottom:'4px' }}>{r.nome}</div>
                {r.plano_descricao && <div style={{ fontSize:'12px', color:'#15803d', marginBottom:'8px' }}>{r.plano_descricao}</div>}
                <div style={{ display:'flex', flexWrap:'wrap', gap:'8px' }}>
                  {[
                    { label:'Previstas', value: r.aulas_previstas ?? '—' },
                    { label:'Realizadas', value: parseInt(r.realizadas||0), color:'#0b8043' },
                    { label:'Cancelou', value: parseInt(r.cancelamentos||0), color:'#e37400' },
                    { label:'Faltou', value: parseInt(r.faltas||0), color:'#c5221f' },
                    { label:'Recuperou', value: parseInt(r.recuperacoes||0), color:'#d56e0c' },
                    { label:'Créditos', value: parseInt(r.creditos_disponiveis||0), color:'#1d4ed8' },
                    r.aulas_restantes != null ? { label:'Restantes', value: parseInt(r.aulas_restantes), color: PRIMARY } : null,
                  ].filter(Boolean).map((s, j) => (
                    <div key={j} style={{ textAlign:'center', minWidth:'55px' }}>
                      <div style={{ fontWeight:800, fontSize:'18px', color: s.color || '#374151' }}>{s.value}</div>
                      <div style={{ fontSize:'10px', color:'#94a3b8' }}>{s.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          // Desktop: tabela
          <div style={{ overflowX:'auto' }}>
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'13px' }}>
              <thead>
                <tr style={{ background:'#f8fafc' }}>
                  {COLUNAS.map(c => (
                    <th key={c.key} style={{
                      padding:'10px 12px', textAlign: c.key === 'nome' ? 'left' : 'center',
                      fontWeight:600, color:'#475569', borderBottom:'2px solid #e2e8f0',
                      whiteSpace:'nowrap', fontSize:'12px',
                    }}>{c.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {dados.map((r, i) => (
                  <tr key={i} style={{ borderBottom:'1px solid #f1f5f9' }}
                    onMouseEnter={e => e.currentTarget.style.background='#f8fafc'}
                    onMouseLeave={e => e.currentTarget.style.background='white'}
                  >
                    <td style={{ padding:'10px 12px', fontWeight:600, color:'#1e293b' }}>
                      {r.nome}
                      {r.plano_descricao && <div style={{ fontSize:'11px', color:'#15803d', fontWeight:400 }}>{r.plano_descricao}</div>}
                    </td>
                    <td style={{ padding:'10px 12px', textAlign:'center', color:'#64748b', fontSize:'12px' }}>{r.plano_descricao || '—'}</td>
                    <td style={{ padding:'10px 12px', textAlign:'center', fontWeight:700 }}>{r.aulas_previstas ?? '—'}</td>
                    <td style={{ padding:'10px 12px', textAlign:'center', fontWeight:700, color:'#0b8043' }}>{parseInt(r.realizadas||0)}</td>
                    <td style={{ padding:'10px 12px', textAlign:'center', fontWeight:700, color:'#e37400' }}>{parseInt(r.cancelamentos||0)}</td>
                    <td style={{ padding:'10px 12px', textAlign:'center', fontWeight:700, color:'#c5221f' }}>{parseInt(r.faltas||0)}</td>
                    <td style={{ padding:'10px 12px', textAlign:'center', fontWeight:700, color:'#d56e0c' }}>{parseInt(r.recuperacoes||0)}</td>
                    <td style={{ padding:'10px 12px', textAlign:'center', fontWeight:700, color:'#1d4ed8' }}>{parseInt(r.creditos_gerados_mes||0)}</td>
                    <td style={{ padding:'10px 12px', textAlign:'center', fontWeight:700, color:'#1d4ed8' }}>{parseInt(r.creditos_disponiveis||0)}</td>
                    <td style={{ padding:'10px 12px', textAlign:'center', fontWeight:800, color: PRIMARY }}>
                      {r.aulas_restantes != null ? parseInt(r.aulas_restantes) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
              {totais && dados.length > 1 && (
                <tfoot>
                  <tr style={{ background:'#f8fafc', borderTop:'2px solid #e2e8f0', fontWeight:800 }}>
                    <td style={{ padding:'10px 12px', color:'#1e293b' }}>TOTAL</td>
                    <td />
                    <td style={{ padding:'10px 12px', textAlign:'center' }}>{totais.aulas_previstas || '—'}</td>
                    <td style={{ padding:'10px 12px', textAlign:'center', color:'#0b8043' }}>{totais.realizadas}</td>
                    <td style={{ padding:'10px 12px', textAlign:'center', color:'#e37400' }}>{totais.cancelamentos}</td>
                    <td style={{ padding:'10px 12px', textAlign:'center', color:'#c5221f' }}>{totais.faltas}</td>
                    <td style={{ padding:'10px 12px', textAlign:'center', color:'#d56e0c' }}>{totais.recuperacoes}</td>
                    <td style={{ padding:'10px 12px', textAlign:'center', color:'#1d4ed8' }}>{totais.creditos_gerados_mes}</td>
                    <td style={{ padding:'10px 12px', textAlign:'center', color:'#1d4ed8' }}>{totais.creditos_disponiveis}</td>
                    <td style={{ padding:'10px 12px', textAlign:'center', color: PRIMARY }}>{totais.aulas_restantes}</td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        )
      )}
    </div>
  );
};

// ── Componente principal ──────────────────────────────────────────────────────
export const AcompanhamentoAlunosFixos = ({ createApi, isMobile }) => {
  const [view, setView]               = useState('lista'); // 'lista' | 'painel' | 'relatorio'
  const [alunoSel, setAlunoSel]       = useState(null);
  const [alunos, setAlunos]           = useState([]);
  const [resumo, setResumo]           = useState([]);
  const [loading, setLoading]         = useState(false);
  const [mes]                         = useState(mesAtual());

  const carregarAlunos = useCallback(async () => {
    setLoading(true);
    try {
      const api = createApi();
      const [alunosRes, resumoRes] = await Promise.all([
        api.get('/admin/alunos-fixos'),
        api.get(`/admin/relatorios/frequencia-mensal?mes=${mes}`),
      ]);
      // Deduplica por aluno_id (cada aluno pode ter múltiplos slots)
      const seen = new Set();
      const unique = alunosRes.data.filter(a => {
        if (seen.has(a.aluno_id)) return false;
        seen.add(a.aluno_id); return true;
      });
      setAlunos(unique);
      setResumo(resumoRes.data.relatorio || []);
    } catch { /* silently fail */ }
    finally { setLoading(false); }
  }, [createApi, mes]);

  useEffect(() => { carregarAlunos(); }, [carregarAlunos]);

  const handleSelect = (aluno) => {
    setAlunoSel(aluno);
    setView('painel');
  };

  return (
    <div>
      {/* Tabs principais */}
      {view !== 'painel' && (
        <div style={{
          display:'flex', gap:'4px', padding: isMobile ? '10px 12px 0' : '14px 20px 0',
          borderBottom:'1px solid #f1f5f9', background:'#fafbfc',
        }}>
          {[
            { id:'lista',    label:'👥 Alunos' },
            { id:'relatorio', label:'📊 Relatório Mensal' },
          ].map(t => (
            <button key={t.id} onClick={() => setView(t.id)} style={{
              padding: isMobile ? '8px 12px' : '10px 16px',
              borderRadius:'10px 10px 0 0', border:'none', cursor:'pointer',
              fontSize: isMobile ? '0.78rem' : '0.85rem',
              fontWeight: view === t.id ? 700 : 500,
              whiteSpace:'nowrap',
              background: view === t.id ? 'white' : 'transparent',
              color: view === t.id ? PRIMARY : '#64748b',
              borderBottom: view === t.id ? `2px solid ${PRIMARY}` : '2px solid transparent',
              transition:'all 0.15s ease',
            }}>{t.label}</button>
          ))}
        </div>
      )}

      {loading && view === 'lista' && (
        <div style={{ textAlign:'center', padding:'32px', color:'#64748b' }}>Carregando...</div>
      )}

      {!loading && view === 'lista' && (
        <ListaAlunos alunos={alunos} resumo={resumo} isMobile={isMobile} onSelect={handleSelect} />
      )}

      {view === 'painel' && alunoSel && (
        <PainelAluno
          aluno={alunoSel} createApi={createApi} isMobile={isMobile}
          onBack={() => { setView('lista'); setAlunoSel(null); }}
        />
      )}

      {view === 'relatorio' && (
        <Relatorio createApi={createApi} isMobile={isMobile} />
      )}
    </div>
  );
};
